import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// @mui
import { LoadingButton } from '@mui/lab';
import { Alert, IconButton, InputAdornment, Link, Stack, Divider, Box, Typography } from '@mui/material';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFCheckbox, RHFTextField } from '../../../components/hook-form';
import GoogleLoginButton from '../../../components/GoogleLoginButton';
import { track } from '../../../utils/analytics';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const { login } = useAuth();

  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);

  // Handler for Google login success
  const handleGoogleSuccess = (data) => {
    track('user_logged_in', { method: 'google' });
    // Google login automatically creates/logs in the user
    // Get current language from URL path (e.g., /en/auth/login -> en)
    const pathParts = window.location.pathname.split('/');
    const currentLang = pathParts[1];
    const lang = ['en', 'es'].includes(currentLang) ? currentLang : 'es';

    // Redirect to dashboard with language prefix
    window.location.href = `/${lang}/dashboard`;
  };

  // Handler for Google login error
  const handleGoogleError = (error) => {
    // Error handled silently
  };

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email debe ser una dirección valida').required('Email es requerido'),
    password: Yup.string().required('Contraseña es requerida'),
  });

  const defaultValues = {
    email: '',
    password: '',
    remember: true,
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    try {
      await login(data.email, data.password);
    } catch (e) {
      setError('afterSubmit', { ...e, message: 'Email o contraseña incorrectos' });

      // reset();
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        {/* Google OAuth Login */}
        <GoogleLoginButton
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          fullWidth
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Divider sx={{ flex: 1 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', px: 1 }}>
            O inicia con email
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Box>

        <RHFTextField name="email" label="Email" placeholder="tucorreo@tudominio.com" />

        <RHFTextField
          name="password"
          label="Contraseña"
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
        <RHFCheckbox name="remember" label="Guardar sesión" />
        <Link component={RouterLink} variant="subtitle2" textAlign='right' to={PATH_AUTH.resetPassword}>
          ¿Olvidaste tu contraseña?
        </Link>
      </Stack>

      <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
        Ingresar
      </LoadingButton>
    </FormProvider>
  );
}
